import { sendWhatsApp } from "@/services/whatsappNotification.service";
import { sendCompanyNotificationEmail } from "@/services/companyEmailNotification.service";

export function toText(value) {
  if (value === null || value === undefined || value === "") return "N/A";
  return String(value);
}

/** e.g. UTC+5:30 from the viewer's local offset at `date`. */
function formatUtcOffsetLabel(date) {
  const offsetMin = -date.getTimezoneOffset();
  const sign = offsetMin >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMin);
  const ah = Math.floor(abs / 60);
  const am = abs % 60;
  if (am === 0) return `UTC${sign}${ah}`;
  return `UTC${sign}${ah}:${String(am).padStart(2, "0")}`;
}

/** Short or generic zone name when Intl provides one (e.g. IST, EST, GMT+5:30). */
function formatTimeZoneAbbreviation(date, ianaTimeZone) {
  if (!ianaTimeZone || ianaTimeZone === "local") return "";
  for (const timeZoneName of ["shortGeneric", "short", "longGeneric"]) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: ianaTimeZone,
        timeZoneName,
      }).formatToParts(date);
      const tzPart = parts.find((p) => p.type === "timeZoneName");
      if (tzPart?.value) return String(tzPart.value).trim();
    } catch {
      /* continue */
    }
  }
  return "";
}

/**
 * Local wall-clock time for SMS/WhatsApp — US style (MM/DD/YYYY, 12-hour time),
 * plus zone (IANA, abbreviation if any, UTC offset).
 */
export function formatMessageDateTime(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  let base;
  try {
    base = new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
  } catch {
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const da = String(d.getDate()).padStart(2, "0");
    let h24 = d.getHours();
    const ampm = h24 >= 12 ? "PM" : "AM";
    let h12 = h24 % 12;
    if (h12 === 0) h12 = 12;
    const mi = String(d.getMinutes()).padStart(2, "0");
    base = `${mo}/${da}/${y}, ${h12}:${mi} ${ampm}`;
  }

  let iana = "local";
  try {
    iana = Intl.DateTimeFormat().resolvedOptions().timeZone || "local";
  } catch {
    /* keep local */
  }

  const offset = formatUtcOffsetLabel(d);
  if (iana === "local") {
    return `${base} (${offset}, device local time)`;
  }

  const abbr = formatTimeZoneAbbreviation(d, iana);
  const offsetNorm = offset.replace(/\s/g, "");
  const abbrNorm = abbr.replace(/\s/g, "").toUpperCase();
  const redundant =
    !abbr ||
    abbrNorm === offsetNorm ||
    (abbr.startsWith("GMT") && offset.startsWith("UTC"));

  const parts = redundant
    ? [`${iana}`, `${offset}`]
    : [`${abbr}`, `${iana}`, `${offset}`];
  return `${base} (${parts.join(" · ")})`;
}

/**
 * Calendar-only value for messages — always YYYY-MM-DD when parseable.
 * Accepts YYYY-MM-DD, MM/DD/YYYY (same as contract date pickers), or Date.
 */
export function formatMessageDateOnly(value) {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    const y = value.getFullYear();
    const mo = String(value.getMonth() + 1).padStart(2, "0");
    const da = String(value.getDate()).padStart(2, "0");
    return `${y}-${mo}-${da}`;
  }
  const s = String(value).trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  if (s.includes("/")) {
    const parts = s.split("/").map((p) => p.trim());
    if (parts.length === 3) {
      const month = parseInt(parts[0], 10);
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      if (
        Number.isFinite(month) &&
        Number.isFinite(day) &&
        Number.isFinite(year) &&
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= 31
      ) {
        return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      }
    }
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const da = String(d.getDate()).padStart(2, "0");
    return `${y}-${mo}-${da}`;
  }
  return s;
}

/**
 * Calendar date for SMS/WhatsApp in US format (MM/DD/YYYY), from ISO or MM/DD/YYYY input.
 */
export function formatMessageDisplayCalendarDate(value) {
  const iso = formatMessageDateOnly(value);
  if (!iso) {
    const t = value != null && String(value).trim();
    return t ? String(value).trim() : "";
  }
  const segs = iso.split("-");
  if (segs.length !== 3) return iso;
  const y = parseInt(segs[0], 10);
  const m = parseInt(segs[1], 10);
  const day = parseInt(segs[2], 10);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(day)) return iso;
  const dt = new Date(y, m - 1, day);
  if (Number.isNaN(dt.getTime())) return iso;
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== day) return iso;
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }).format(dt);
  } catch {
    return `${String(m).padStart(2, "0")}/${String(day).padStart(2, "0")}/${y}`;
  }
}

export function formatBoolean(value) {
  return value ? "Yes" : "No";
}

export function formatCurrency(value) {
  if (value === null || value === undefined || value === "") return "N/A";
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return toText(value);
  return `$${numericValue.toFixed(2)}`;
}

export function hasValue(value) {
  return (
    value !== null &&
    value !== undefined &&
    String(value).trim() !== "" &&
    String(value).trim() !== "N/A"
  );
}

export function hasMeaningfulCurrency(value) {
  if (value === null || value === undefined || value === "") return false;
  const n = Number(value);
  return !Number.isNaN(n);
}

export function sectionLines(title, lines) {
  if (!lines.length) return [];
  return ["", title, ...lines];
}

export function getCustomerNameById(customers, customerId) {
  if (!customerId) return "N/A";
  const selectedCustomer = customers.find(
    (customer) => String(customer.id) === String(customerId)
  );
  return selectedCustomer?.customerName || selectedCustomer?.name || "N/A";
}

export function getVendorNameById(vendors, vendorId) {
  if (!vendorId) return "N/A";
  const selectedVendor = vendors.find(
    (vendor) => String(vendor.id) === String(vendorId)
  );
  return selectedVendor?.vendorName || selectedVendor?.name || "N/A";
}

export function getPaymentTermNameById(paymentTerms, paymentTermId) {
  if (!paymentTermId) return "N/A";
  const paymentTerm = paymentTerms.find(
    (term) => String(term.id) === String(paymentTermId)
  );
  return paymentTerm?.name || "N/A";
}

export function getInvoiceFrequencyNameById(
  invoiceFrequencies,
  invoicingFrequencyId
) {
  if (!invoicingFrequencyId) return "N/A";
  const frequency = invoiceFrequencies.find(
    (freq) => String(freq.id) === String(invoicingFrequencyId)
  );
  return frequency?.name || "N/A";
}

export function getTimesheetFrequencyNameById(
  timesheetFrequencies,
  timesheetFrequencyId
) {
  if (!timesheetFrequencyId) return "N/A";
  const frequency = (timesheetFrequencies || []).find(
    (freq) => String(freq.id) === String(timesheetFrequencyId)
  );
  return frequency?.name || "N/A";
}

export function getCompanyAccountLabel(accounts, accountId) {
  if (!accountId) return "N/A";
  const acc = (accounts || []).find((a) => String(a.id) === String(accountId));
  if (!acc) return toText(accountId);
  const num =
    acc.accountNumber != null && acc.accountNumber !== ""
      ? toText(acc.accountNumber)
      : "";
  const name =
    acc.accountName != null && acc.accountName !== ""
      ? toText(acc.accountName)
      : "";
  const typ =
    acc.accountType != null && acc.accountType !== ""
      ? toText(acc.accountType)
      : "";
  if (num && name)
    return typ ? `${num} - ${name} (${typ})` : `${num} - ${name}`;
  return name || num || "N/A";
}

export function optionalLine(label, displayValue) {
  if (!hasValue(displayValue) || displayValue === "N/A") return null;
  return `- ${label}: ${displayValue}`;
}

export function buildContractSummaryLines(hasContractData, contract) {
  const lines = [];

  const hasStrayContractFields =
    hasValue(contract?.contractNo) ||
    hasValue(contract?.contractType) ||
    hasValue(contract?.startDate) ||
    hasValue(contract?.endDate) ||
    hasValue(contract?.status) ||
    hasValue(contract?.title);

  if (!hasContractData) {
    if (!hasStrayContractFields) {
      return lines;
    }
    lines.push(`- Contract added: ${formatBoolean(false)}`);
    return lines;
  }

  lines.push(`- Contract added: ${formatBoolean(true)}`);
  const no = optionalLine("Contract no.", contract?.contractNo);
  if (no) lines.push(no);
  const ct = optionalLine("Contract type", contract?.contractType);
  if (ct) lines.push(ct);
  const sd = optionalLine(
    "Contract start date",
    formatMessageDisplayCalendarDate(contract?.startDate) || null
  );
  if (sd) lines.push(sd);
  const ed = optionalLine(
    "Contract end date",
    formatMessageDisplayCalendarDate(contract?.endDate) || null
  );
  if (ed) lines.push(ed);
  const st = optionalLine("Contract status", contract?.status);
  if (st) lines.push(st);
  const tl = optionalLine("Contract title", contract?.title);
  if (tl) lines.push(tl);

  return lines;
}

export async function sendConsultantOnboardingWhatsApp(message, sendSMS, companyId) {
  try {
    await sendWhatsApp(message, Boolean(sendSMS), {
      ...(companyId != null && companyId !== "" ? { companyId } : {}),
    });
  } catch {
    /* errors logged in sendWhatsApp */
  }
  await sendCompanyNotificationEmail(
    message,
    companyId,
    "Anna Books: New consultant",
    { event: "consultant" }
  );
}
